/**
 * API base-URL resolution (#847).
 *
 * Single source of truth for where the SPA sends API requests. Every consumer
 * (the shared axios client, the form-encoded login call, the signup debug
 * dump) must go through `resolveApiBaseUrl()` so one env-var rename can never
 * silently desync them again.
 *
 * Precedence, first non-empty wins:
 *
 * 1. `window.APP_CONFIG.API_BASE_URL` — runtime `config.js` written by
 *    `docker-entrypoint.sh` from the *container* env (`VITE_API_URL` in
 *    Infisical / docker-compose). Wins so a deployed image never needs a
 *    rebuild to point at a different backend. Absent under `npm run dev`.
 * 2. `import.meta.env.VITE_API_BASE_URL` — the documented build-time name.
 *    `.env.development` points it at the compose backend (`:8082`).
 * 3. `import.meta.env.VITE_API_URL` — the pre-#847 build-time name, kept so an
 *    existing `.env.*.local` override keeps working. Dev builds warn once so
 *    the file gets renamed rather than forgotten.
 * 4. `DEFAULT_API_BASE_URL` — a bare `uvicorn` on `:8000`. Dev builds warn
 *    once, because silently talking to whatever happens to listen on that
 *    port is exactly how #847 was found.
 */
export const DEFAULT_API_BASE_URL = "http://localhost:8000";

// The env is static for the lifetime of a page, so a single flag is enough to
// keep the diagnostic to one line per load however often the resolver runs.
let warned = false;

// Deliberately `console.warn` rather than the structured logger: this is a
// bootstrap diagnostic that must surface even when LOG_LEVEL is raised, and
// it only ever fires in `npm run dev` / `vite build --mode development`.
const warnOnce = (message: string): void => {
  if (!import.meta.env.DEV || warned) {
    return;
  }
  warned = true;
  console.warn(`[api] ${message}`);
};

export const resolveApiBaseUrl = (): string => {
  if (typeof window !== "undefined" && window.APP_CONFIG?.API_BASE_URL) {
    return window.APP_CONFIG.API_BASE_URL;
  }

  const documented = import.meta.env.VITE_API_BASE_URL;
  if (documented) {
    return documented;
  }

  const legacy = import.meta.env.VITE_API_URL;
  if (legacy) {
    warnOnce(
      `Resolved the API base URL from VITE_API_URL (${legacy}). ` +
        "That name is deprecated — rename it to VITE_API_BASE_URL in your .env.*.local."
    );
    return legacy;
  }

  warnOnce(
    `No VITE_API_BASE_URL set — falling back to ${DEFAULT_API_BASE_URL}. ` +
      "Set VITE_API_BASE_URL (see .env.development for the docker-compose value)."
  );
  return DEFAULT_API_BASE_URL;
};
